import type { Rect } from "../scene/geometry";
import type { OverlayBox, OverlayClip, SceneDocument, VideoClip } from "../scene/schema";
import { type FrameSize, videoContentInsets, videoOrientation } from "./frame";
import {
  addOverlayClip,
  clipAt,
  clipDuration,
  createOverlayClip,
  layoutClips,
  mainTrackDuration,
  overlayClipEnd,
  overlayClips,
} from "./timeline";

/**
 * Screen layouts of video documents ("Bildaufteilung"): split screens, picture
 * in picture, grids and cards in one click. Slot 0 is the main track clip
 * (its `box`), the other slots are clips on the video tracks above it.
 */

export const SCENE_LAYOUT_IDS = [
  // One picture
  "full",
  "card",
  "frame",
  "letterbox",
  "top-title",
  "polaroid",
  // Two pictures
  "top-bottom",
  "left-right",
  "reaction",
  "pip-bottom-right",
  "pip-bottom-left",
  "pip-top-left",
  "pip-top-right",
  "facecam",
  "facecam-right",
  // Three or four
  "one-two",
  "two-one",
  "rows-3",
  "one-three",
  "grid-4",
] as const;
export type SceneLayoutId = (typeof SCENE_LAYOUT_IDS)[number];

export interface SceneLayout {
  id: SceneLayoutId;
  label: string;
  description: string;
  /** Number of pictures (main clip + clips above). */
  slots: number;
  /** Always rounded and inset (cards, frames); otherwise only with "Abstand". */
  framed?: boolean;
  /** A small picture over the full frame (picture in picture, facecam). */
  pip?: boolean;
}

export const SCENE_LAYOUTS: readonly SceneLayout[] = [
  { id: "full", label: "Vollbild", description: "Das Video füllt das ganze Bild", slots: 1 },
  {
    id: "card",
    label: "Karte",
    description: "Video als abgerundete Karte auf der Hintergrundfarbe",
    slots: 1,
    framed: true,
  },
  {
    id: "frame",
    label: "Rahmen",
    description: "Video mit schmalem Rand in der Hintergrundfarbe",
    slots: 1,
    framed: true,
  },
  {
    id: "letterbox",
    label: "Kino",
    description:
      "Querformat-Video als Band in der Mitte, oben und unten Platz für Text (Hintergrundfarbe oder unscharf)",
    slots: 1,
  },
  {
    id: "top-title",
    label: "Titel oben",
    description: "Video in den unteren zwei Dritteln, oben Platz für eine Überschrift",
    slots: 1,
  },
  {
    id: "polaroid",
    label: "Foto",
    description: "Quadratisches Bild mit Rand und Platz für Text darunter",
    slots: 1,
    framed: true,
  },
  {
    id: "top-bottom",
    label: "Oben / unten",
    description: "Zwei Videos übereinander (Splitscreen)",
    slots: 2,
  },
  {
    id: "left-right",
    label: "Links / rechts",
    description: "Zwei Videos nebeneinander",
    slots: 2,
  },
  {
    id: "reaction",
    label: "Reaktion",
    description: "Oben groß der Inhalt, unten die Reaktion",
    slots: 2,
  },
  {
    id: "pip-bottom-right",
    label: "Bild im Bild",
    description: "Kleines Video unten rechts",
    slots: 2,
    pip: true,
  },
  {
    id: "pip-bottom-left",
    label: "Bild im Bild unten links",
    description: "Kleines Video unten links",
    slots: 2,
    pip: true,
  },
  {
    id: "pip-top-left",
    label: "Bild im Bild oben links",
    description: "Kleines Video oben links",
    slots: 2,
    pip: true,
  },
  {
    id: "pip-top-right",
    label: "Bild im Bild oben rechts",
    description: "Kleines Video oben rechts",
    slots: 2,
    pip: true,
  },
  {
    id: "facecam",
    label: "Kreis links",
    description: "Runde Facecam unten links",
    slots: 2,
    pip: true,
  },
  {
    id: "facecam-right",
    label: "Kreis rechts",
    description: "Runde Facecam unten rechts",
    slots: 2,
    pip: true,
  },
  { id: "one-two", label: "1 + 2", description: "Oben ein Video, unten zwei", slots: 3 },
  { id: "two-one", label: "2 + 1", description: "Oben zwei Videos, unten eins", slots: 3 },
  { id: "rows-3", label: "Drei Reihen", description: "Drei Videos übereinander", slots: 3 },
  {
    id: "one-three",
    label: "1 + 3",
    description: "Oben groß ein Video, unten drei kleine",
    slots: 4,
  },
  { id: "grid-4", label: "Raster 2 × 2", description: "Vier Videos im Raster", slots: 4 },
];

export function getSceneLayout(id: string): SceneLayout | undefined {
  return SCENE_LAYOUTS.find((layout) => layout.id === id);
}

/** Labels of layouts that turn sideways in landscape formats. */
const LANDSCAPE_LABELS: Partial<Record<SceneLayoutId, Pick<SceneLayout, "label" | "description">>> =
  {
    "left-right": {
      label: "Links / rechts",
      description: "Zwei Videos nebeneinander (Splitscreen)",
    },
    reaction: { label: "Reaktion", description: "Links groß der Inhalt, rechts die Reaktion" },
    "one-two": { label: "1 + 2", description: "Links ein Video, rechts zwei" },
    "rows-3": { label: "Drei Spalten", description: "Drei Videos nebeneinander" },
    "two-one": { label: "2 + 1", description: "Links zwei Videos, rechts eins" },
    "one-three": { label: "1 + 3", description: "Links groß ein Video, rechts drei kleine" },
    letterbox: { label: "Kino", description: "Breitbild mit Balken oben und unten (21:9)" },
  };

/**
 * The layouts offered for a format: in landscape the splits run side by
 * side (two strips on top of each other would be far too flat).
 */
export function sceneLayoutsForFrame(frame: FrameSize): SceneLayout[] {
  if (videoOrientation(frame) !== "landscape") return [...SCENE_LAYOUTS];
  return SCENE_LAYOUTS.filter((layout) => layout.id !== "top-bottom").map((layout) => ({
    ...layout,
    ...LANDSCAPE_LABELS[layout.id],
  }));
}

export interface SceneLayoutGroup {
  label: string;
  layouts: SceneLayout[];
}

/** Layouts by number of pictures, for the layout picker ("1 Bild", "2 Bilder", "3 oder 4 Bilder"). */
export function groupSceneLayouts(layouts: readonly SceneLayout[]): SceneLayoutGroup[] {
  const groups: SceneLayoutGroup[] = [
    { label: "1 Bild", layouts: layouts.filter((layout) => layout.slots === 1) },
    { label: "2 Bilder", layouts: layouts.filter((layout) => layout.slots === 2) },
    { label: "3 oder 4 Bilder", layouts: layouts.filter((layout) => layout.slots > 2) },
  ];
  return groups.filter((group) => group.layouts.length > 0);
}

/**
 * The layout that does the same job in another format: a split screen stays
 * a split screen when the video turns from portrait to landscape.
 */
export function equivalentSceneLayout(
  id: SceneLayoutId,
  from: FrameSize,
  to: FrameSize,
): SceneLayoutId {
  const before = videoOrientation(from);
  const after = videoOrientation(to);
  if (id === "top-bottom" && after === "landscape" && before !== "landscape") return "left-right";
  if (id === "left-right" && after === "portrait" && before === "landscape") return "top-bottom";
  return id;
}

export interface LayoutSlotBox {
  box: OverlayBox;
  radius: number;
}

export interface LayoutOptions {
  /** Gaps between the pictures and rounded corners. */
  spaced?: boolean;
}

/** Splits [0, total) into `count` cells with `gap` between them and `margin` outside. */
function cells(total: number, count: number, gap: number, margin: number): [number, number][] {
  const size = (total - margin * 2 - gap * (count - 1)) / count;
  return Array.from({ length: count }, (_, index) => [margin + index * (size + gap), size]);
}

const box = (x: number, y: number, width: number, height: number): OverlayBox => ({
  x: Math.round(x),
  y: Math.round(y),
  width: Math.max(10, Math.round(width)),
  height: Math.max(10, Math.round(height)),
});

/** Where each picture of a layout goes on a frame (slot 0 = main track clip). */
export function sceneLayoutBoxes(
  id: SceneLayoutId,
  frame: { width: number; height: number },
  options: LayoutOptions = {},
): LayoutSlotBox[] {
  const W = frame.width;
  const H = frame.height;
  const short = Math.min(W, H);
  const spaced = Boolean(options.spaced);
  const gap = spaced ? Math.round(short * 0.025) : 0;
  const radius = spaced ? Math.round(short * 0.035) : 0;
  // Square frames stack like portrait ones.
  const portrait = H >= W;
  const orientation = videoOrientation(frame);
  const plain = (boxes: OverlayBox[], r = radius) =>
    boxes.map((entry) => ({ box: entry, radius: r }));
  const rows = (count: number) =>
    cells(H, count, gap, gap).map(([y, h]) => box(gap, y, W - gap * 2, h));
  const columns = (count: number) =>
    cells(W, count, gap, gap).map(([x, w]) => box(x, gap, w, H - gap * 2));
  // Small pictures stay clear of the app interface at the top and the captions at the bottom.
  const margin = Math.round(short * 0.05);
  const insets = videoContentInsets(frame);
  const pipWidth = orientation === "square" ? W * 0.36 : portrait ? W * 0.42 : W * 0.3;
  const pipHeight = portrait ? pipWidth * 1.25 : (pipWidth * 9) / 16;
  const pipRadius = Math.round(short * 0.035);
  const top = insets.top;
  const bottom = H - insets.bottom;
  switch (id) {
    case "full":
      return plain([box(0, 0, W, H)], 0);
    case "top-bottom":
      return plain(rows(2));
    case "left-right":
      return plain(columns(2));
    case "reaction": {
      if (!portrait) {
        const main = (W - gap * 3) * 0.6;
        return plain([
          box(gap, gap, main, H - gap * 2),
          box(gap * 2 + main, gap, W - gap * 3 - main, H - gap * 2),
        ]);
      }
      const main = (H - gap * 3) * 0.6;
      return plain([
        box(gap, gap, W - gap * 2, main),
        box(gap, gap * 2 + main, W - gap * 2, H - gap * 3 - main),
      ]);
    }
    case "pip-bottom-right":
      return [
        { box: box(0, 0, W, H), radius: 0 },
        {
          box: box(W - margin - pipWidth, bottom - pipHeight, pipWidth, pipHeight),
          radius: pipRadius,
        },
      ];
    case "pip-bottom-left":
      return [
        { box: box(0, 0, W, H), radius: 0 },
        { box: box(margin, bottom - pipHeight, pipWidth, pipHeight), radius: pipRadius },
      ];
    case "pip-top-left":
      return [
        { box: box(0, 0, W, H), radius: 0 },
        { box: box(margin, top, pipWidth, pipHeight), radius: pipRadius },
      ];
    case "pip-top-right":
      return [
        { box: box(0, 0, W, H), radius: 0 },
        { box: box(W - margin - pipWidth, top, pipWidth, pipHeight), radius: pipRadius },
      ];
    case "facecam":
    case "facecam-right": {
      const size = Math.round(short * (portrait ? 0.4 : 0.3));
      const x = id === "facecam" ? margin : W - margin - size;
      return [
        { box: box(0, 0, W, H), radius: 0 },
        { box: box(x, bottom - size, size, size), radius: size / 2 },
      ];
    }
    case "one-two": {
      if (!portrait) {
        const [left, right] = cells(W, 2, gap, gap) as [[number, number], [number, number]];
        return plain([
          box(left[0], gap, left[1], H - gap * 2),
          ...cells(H, 2, gap, gap).map(([y, h]) => box(right[0], y, right[1], h)),
        ]);
      }
      const [upper, lower] = cells(H, 2, gap, gap) as [[number, number], [number, number]];
      const halves = cells(W, 2, gap, gap);
      return plain([
        box(gap, upper[0], W - gap * 2, upper[1]),
        ...halves.map(([x, w]) => box(x, lower[0], w, lower[1])),
      ]);
    }
    case "two-one": {
      if (!portrait) {
        const [left, right] = cells(W, 2, gap, gap) as [[number, number], [number, number]];
        return plain([
          ...cells(H, 2, gap, gap).map(([y, h]) => box(left[0], y, left[1], h)),
          box(right[0], gap, right[1], H - gap * 2),
        ]);
      }
      const [upper, lower] = cells(H, 2, gap, gap) as [[number, number], [number, number]];
      return plain([
        ...cells(W, 2, gap, gap).map(([x, w]) => box(x, upper[0], w, upper[1])),
        box(gap, lower[0], W - gap * 2, lower[1]),
      ]);
    }
    case "rows-3":
      return plain(portrait ? rows(3) : columns(3));
    case "one-three": {
      // The big picture takes two thirds; the three small ones share the rest (9:16 / 16:9).
      if (!portrait) {
        const main = ((W - gap * 3) * 2) / 3;
        const x = gap * 2 + main;
        return plain([
          box(gap, gap, main, H - gap * 2),
          ...cells(H, 3, gap, gap).map(([y, h]) => box(x, y, W - gap - x, h)),
        ]);
      }
      const main = ((H - gap * 3) * 2) / 3;
      const y = gap * 2 + main;
      return plain([
        box(gap, gap, W - gap * 2, main),
        ...cells(W, 3, gap, gap).map(([x, w]) => box(x, y, w, H - gap - y)),
      ]);
    }
    case "grid-4": {
      const ys = cells(H, 2, gap, gap);
      const xs = cells(W, 2, gap, gap);
      return plain(ys.flatMap(([y, h]) => xs.map(([x, w]) => box(x, y, w, h))));
    }
    case "card": {
      const inset = Math.round(W * 0.07);
      const height = portrait ? Math.min(H * 0.62, (W - inset * 2) * 1.25) : H - inset * 2;
      return [
        {
          box: box(inset, (H - height) / 2, W - inset * 2, height),
          radius: Math.round(short * 0.05),
        },
      ];
    }
    case "frame": {
      const inset = Math.round(short * 0.04);
      return [
        { box: box(inset, inset, W - inset * 2, H - inset * 2), radius: Math.round(short * 0.04) },
      ];
    }
    case "letterbox": {
      // Landscape footage in a 16:9 band; landscape frames get cinema bars (21:9).
      const width = W - gap * 2;
      const height = Math.min(
        H - gap * 2,
        orientation === "landscape" ? width / 2.39 : (width * 9) / 16,
      );
      return [{ box: box(gap, (H - height) / 2, width, height), radius }];
    }
    case "top-title": {
      const y = Math.round(H / 3);
      return [{ box: box(gap, y, W - gap * 2, H - y - gap), radius }];
    }
    case "polaroid": {
      // Square picture, a little above the middle: below it room for a line of text.
      const side = Math.min(W * 0.8, H * 0.62);
      return [
        {
          box: box((W - side) / 2, (H - side) * 0.4, side, side),
          radius: Math.round(short * 0.015),
        },
      ];
    }
  }
}

function isFullFrame(slot: LayoutSlotBox, frame: { width: number; height: number }): boolean {
  const { box: b } = slot;
  return (
    slot.radius <= 0 &&
    Math.abs(b.x) < 1 &&
    Math.abs(b.y) < 1 &&
    Math.abs(b.width - frame.width) < 1 &&
    Math.abs(b.height - frame.height) < 1
  );
}

/** Puts a main track clip into a slot (the whole frame removes the box). */
function placeMain(clip: VideoClip, slot: LayoutSlotBox, frame: { width: number; height: number }) {
  if (isFullFrame(slot, frame)) {
    delete clip.box;
    delete clip.radius;
  } else {
    clip.box = { ...slot.box };
    if (slot.radius > 0) clip.radius = slot.radius;
    else delete clip.radius;
  }
  clip.framing = { fit: "cover", zoom: 1, x: 0, y: 0, flipX: clip.framing.flipX };
}

function placeOverlay(clip: OverlayClip, slot: LayoutSlotBox) {
  clip.box = { ...slot.box };
  clip.radius = slot.radius;
  clip.framing = { fit: "cover", zoom: 1, x: 0, y: 0, flipX: clip.framing.flipX };
}

export interface LayoutResult {
  /** Timeline range the layout covers (the main clip, or the whole main track). */
  start: number;
  end: number;
  /** Clips of the tracks above that were put into slots. */
  placed: string[];
  /** Slots without a picture yet (see `fillLayoutSlots`). */
  missing: LayoutSlotBox[];
  /** Clips of the tracks above in that range that did not get a slot. */
  unused: number;
}

/**
 * Applies a layout at timeline position `t`: the main track clip there (or,
 * with `allClips`, every main track clip) gets slot 0, clips of the tracks
 * above that play at the same time the next slots, bottom track first.
 * Returns null without a main track clip at `t`.
 */
export function applySceneLayout(
  doc: SceneDocument,
  id: SceneLayoutId,
  t: number,
  options: LayoutOptions & { allClips?: boolean } = {},
): LayoutResult | null {
  const timeline = doc.timeline;
  if (!timeline) return null;
  const layout = layoutClips(timeline);
  const entry = clipAt(layout, t);
  if (!entry) return null;
  const slots = sceneLayoutBoxes(id, doc.format, options);
  const range = options.allClips
    ? { start: 0, end: mainTrackDuration(timeline) }
    : { start: entry.start, end: entry.end };
  const mains = options.allClips ? timeline.clips : [timeline.clips[entry.index]!];
  for (const clip of mains) placeMain(clip, slots[0]!, doc.format);
  const candidates = overlayClips(timeline)
    .filter((clip) => clip.start < range.end - 0.5 && overlayClipEnd(clip) > range.start + 0.5)
    .sort((a, b) => a.track - b.track || a.start - b.start);
  // One clip per slot and track: clips after each other on the same track share a slot.
  const byTrack = new Map<number, OverlayClip[]>();
  for (const clip of candidates)
    byTrack.set(clip.track, [...(byTrack.get(clip.track) ?? []), clip]);
  const tracks = [...byTrack.keys()].sort((a, b) => a - b);
  const placed: string[] = [];
  tracks.forEach((track, index) => {
    const slot = slots[index + 1];
    if (!slot) return;
    for (const clip of byTrack.get(track)!) {
      placeOverlay(clip, slot);
      placed.push(clip.id);
    }
  });
  const used = Math.min(tracks.length, slots.length - 1);
  return {
    ...range,
    placed,
    missing: slots.slice(1 + used),
    unused: tracks.slice(used).reduce((sum, track) => sum + byTrack.get(track)!.length, 0),
  };
}

export interface LayoutAsset {
  id: string;
  kind: string;
  durationMs: number | null;
  name?: string;
}

/**
 * Puts media into the empty slots of a layout: one clip per slot on its own
 * track above, playing over the layout's range (videos at most as long as
 * they are, muted so the main track's sound stays). Returns the new clip ids.
 */
export function fillLayoutSlots(
  doc: SceneDocument,
  slots: readonly LayoutSlotBox[],
  assets: readonly LayoutAsset[],
  range: { start: number; end: number },
): string[] {
  const created: string[] = [];
  const length = Math.max(100, range.end - range.start);
  slots.forEach((slot, index) => {
    const asset = assets[index];
    if (!asset || (asset.kind !== "video" && asset.kind !== "image" && asset.kind !== "svg"))
      return;
    const clip = createOverlayClip(asset, doc.format, {
      start: Math.round(range.start),
      box: { ...slot.box },
      radius: slot.radius,
      muted: asset.kind === "video",
      track: 1,
    });
    const available = clip.kind === "video" ? clipDuration(clip) : Number.POSITIVE_INFINITY;
    clip.sourceOut = clip.sourceIn + Math.round(Math.min(available, length));
    addOverlayClip(doc, clip);
    created.push(clip.id);
  });
  return created;
}

/** Bounding rectangle of a slot (for previews of the layout). */
export function slotRect(slot: LayoutSlotBox): Rect {
  return { x: slot.box.x, y: slot.box.y, width: slot.box.width, height: slot.box.height };
}
